import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ChevronDown, User, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WikiHeader({ username, onLogout, showSearch = true }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        setSelectedIndex(-1);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const searchPages = async () => {
      if (searchQuery.length < 1) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      const token = localStorage.getItem("wiki_token");
      if (!token) return;

      try {
        const response = await axios.get(
          `${API}/wiki/search?q=${encodeURIComponent(searchQuery)}&token=${token}`
        );
        setSearchResults(response.data.results || []);
        setShowResults(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
      }
    };

    const debounce = setTimeout(searchPages, 150);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) {
      if (e.key === "Enter" && searchQuery.trim()) {
        // Navigate to search query as page title if no results
        navigate(`/wiki/${encodeURIComponent(searchQuery)}`);
        setShowResults(false);
        setSearchQuery("");
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          navigate(`/wiki/${encodeURIComponent(searchResults[selectedIndex])}`);
          setShowResults(false);
          setSearchQuery("");
          setSelectedIndex(-1);
        } else if (searchQuery.trim()) {
          navigate(`/wiki/${encodeURIComponent(searchQuery)}`);
          setShowResults(false);
          setSearchQuery("");
        }
        break;
      case "Escape":
        setShowResults(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleResultClick = (title) => {
    navigate(`/wiki/${encodeURIComponent(title)}`);
    setShowResults(false);
    setSearchQuery("");
    setSelectedIndex(-1);
  };

  return (
    <div className="bg-white border-b border-[#a7d7f9]">
      <div className="max-w-[1200px] mx-auto px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/wiki" className="flex items-center gap-2 hover:opacity-80">
            <img
              src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png"
              alt="Ethical Wiki"
              className="w-10 h-10"
            />
            <span className="font-wiki text-xl text-[#202122]">Ethical Wiki</span>
          </Link>

          {/* Search Bar */}
          {showSearch && username && (
            <div ref={searchRef} className="relative ml-4">
              <div className="flex items-center">
                <div className="relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => searchQuery.length > 0 && setShowResults(true)}
                    placeholder="Search wiki..."
                    className="w-[280px] px-3 py-1.5 pr-8 text-sm border border-[#a2a9b1] rounded focus:outline-none focus:border-[#3366cc] focus:ring-1 focus:ring-[#3366cc]"
                    data-testid="wiki-search-input"
                  />
                  <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#72777d]" />
                </div>
              </div>

              {/* Autocomplete Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 w-full mt-1 bg-white border border-[#a2a9b1] rounded shadow-lg z-50 max-h-[300px] overflow-y-auto">
                  {searchResults.map((result, index) => (
                    <button
                      key={result}
                      onClick={() => handleResultClick(result)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-[#eaecf0] ${
                        index === selectedIndex ? "bg-[#eaecf0]" : ""
                      }`}
                      data-testid={`wiki-search-result-${index}`}
                    >
                      <span className="text-[#3366cc]">{result}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* No results message */}
              {showResults && searchQuery.length > 0 && searchResults.length === 0 && (
                <div className="absolute top-full left-0 w-full mt-1 bg-white border border-[#a2a9b1] rounded shadow-lg z-50">
                  <div className="px-3 py-2 text-sm text-[#72777d]">
                    No pages found. Press Enter to go to "{searchQuery}"
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 text-sm">
          {username ? (
            <>
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-1 text-[#3366cc] hover:underline"
                  data-testid="wiki-user-menu-btn"
                >
                  <span>{username}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-[#a2a9b1] rounded shadow-lg z-50 min-w-[180px]">
                    <button
                      onClick={() => {
                        navigate(`/wiki/User:${encodeURIComponent(username)}`);
                        setShowUserMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-[#202122] hover:bg-[#eaecf0] flex items-center gap-2"
                      data-testid="wiki-user-page-btn"
                    >
                      <User className="w-4 h-4" />
                      User Page
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/wiki/contributions/${encodeURIComponent(username)}`);
                        setShowUserMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-[#202122] hover:bg-[#eaecf0] flex items-center gap-2 border-t border-[#eaecf0]"
                      data-testid="wiki-contributions-btn"
                    >
                      <Clock className="w-4 h-4" />
                      Contributions
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={onLogout}
                className="text-[#3366cc] hover:underline"
                data-testid="wiki-logout-btn"
              >
                Log out
              </button>
            </>
          ) : (
            <Link
              to="/wiki/login"
              className="text-[#3366cc] hover:underline"
              data-testid="wiki-login-link"
            >
              Log in
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
